import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import useQueryParamsNext from './useQueryParamsNext';

const LEAD_MANAGEMENT_PATH = '/lead-management';

/** Parses `listingAlias` (path or `?listingAlias=foo,bar`); runs the v2 path→query redirect when enabled. */
export default function useLeadManagementListingAliases(enabled: boolean): Array<string> {
  const router = useRouter();
  const { updateQueryParams } = useQueryParamsNext();
  const raw = router.query.listingAlias;

  // Use asPath (not router.query) — Next.js keeps the internal route in path form after the redirect.
  useEffect(() => {
    if (!enabled || !router.isReady) return;
    if (!router.asPath.startsWith(`${LEAD_MANAGEMENT_PATH}/`)) return;
    if (!Array.isArray(raw) || raw.length === 0) return;
    const aliasParam = raw.filter(Boolean).join(',');
    if (!aliasParam) return;
    updateQueryParams({
      queryParamValues: { listingAlias: aliasParam },
      pathname: LEAD_MANAGEMENT_PATH,
      shouldReplaceHistory: true,
    });
  }, [enabled, router.isReady, router.asPath, raw, updateQueryParams]);

  return useMemo(() => {
    const segments = typeof raw === 'string' ? [raw] : Array.isArray(raw) ? raw : [];
    if (!enabled) return segments.filter(Boolean);
    return segments
      .flatMap((segment) => segment.split(','))
      .map((alias) => alias.trim())
      .filter(Boolean);
  }, [raw, enabled]);
}
